import React, { useState, useRef, useEffect, useMemo } from "react";

/**
 * A professional, searchable, unified dropdown component.
 * Supports single and multi-select modes with click-outside dismissal.
 */
export default function CustomSelect({
  label,
  placeholder = "-- Select --",
  options = [], // Can be ["A", "B"] or [{ id: "1", name: "A" }]
  value, // String for single, Array for multiple
  onChange,
  multiple = false,
  isClearable = false,
  style = {},
  className = ""
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  // Normalize options to [{ id, name }]
  const normalizedOptions = useMemo(() => {
    return options.map(opt => {
      if (typeof opt === "string") return { id: opt, name: opt };
      return opt;
    });
  }, [options]);

  const filteredOptions = useMemo(() => {
    const q = search.toLowerCase();
    return normalizedOptions.filter(o => o.name.toLowerCase().includes(q));
  }, [normalizedOptions, search]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = (id) => {
    if (multiple) {
      const current = Array.isArray(value) ? value : [];
      const next = current.includes(id) 
        ? current.filter(x => x !== id) 
        : [...current, id];
      onChange(next);
    } else {
      onChange(id);
      setIsOpen(false);
    }
  };

  const getLabel = () => {
    if (multiple) {
      const current = Array.isArray(value) ? value : [];
      if (current.length === 0) return placeholder;
      return current.map(id => normalizedOptions.find(o => o.id === id)?.name || id).join(", ");
    }
    if (!value) return placeholder;
    return normalizedOptions.find(o => o.id === value)?.name || value;
  };

  const isSelected = (id) => {
    if (multiple) return (Array.isArray(value) ? value : []).includes(id);
    return value === id;
  };

  return (
    <div className={`custom-select-container ${className}`} style={{ position: "relative", zIndex: isOpen ? 9999 : 1, ...style }} ref={containerRef}>
      {label && <label className="form-label">{label}</label>}
      
      <div 
        className={`custom-select-trigger ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`custom-select-value ${!value || (multiple && value.length === 0) ? "placeholder" : ""}`}>
          {getLabel()}
        </span>
        <span className="custom-select-arrow">▼</span>
      </div>

      {isOpen && (
        <div className="custom-select-dropdown">
          <div className="custom-select-search-wrapper">
            <span className="search-icon">🔍</span>
            <input 
              type="text" 
              className="custom-select-search-input"
              placeholder="Search..." 
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
              autoFocus
            />
          </div>

          <div 
            className="custom-select-options"
            style={{ 
              maxHeight: "160px", 
              overflowY: "auto", 
              overflowX: "hidden", 
              paddingBottom: "12px",
              display: "flex",
              flexDirection: "column"
            }}
          >
            {isClearable && !multiple && (
              <div 
                className="custom-select-option clear-option"
                onClick={(e) => { e.stopPropagation(); onChange(""); setIsOpen(false); }}
              >
                -- Clear Selection --
              </div>
            )}

            {filteredOptions.length === 0 && (
              <div className="custom-select-no-results">No options found</div>
            )}

            {filteredOptions.map(opt => (
              <div 
                key={opt.id}
                className={`custom-select-option ${isSelected(opt.id) ? "selected" : ""}`}
                style={{ 
                  flexShrink: 0, 
                  minHeight: "44px", 
                  wordBreak: "break-word",
                  whiteSpace: "normal"
                }}
                onClick={(e) => { e.stopPropagation(); handleToggle(opt.id); }}
              >
                <div className="option-content">
                  {multiple && <input type="checkbox" checked={isSelected(opt.id)} readOnly style={{ marginRight: 8 }} />}
                  <span>{opt.name}</span>
                </div>
                {isSelected(opt.id) && !multiple && <span className="check-mark">✓</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
